import { Injectable, OnDestroy, inject } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { UserInactivityService } from './user-inactivity.service';

@Injectable({
  providedIn: 'root',
})
export class InactivityManagerService implements OnDestroy {
  private router = inject(Router);
  private inactivityService = inject(UserInactivityService);
  private routerSubscription: Subscription | null = null;
  private isInitialized = false;

  // Configura las rutas de logout - ajusta según tu aplicación
  private logoutRoutes: string[] = [
    '/auth/login?returnUrl=%2F',
    '/login',
    '/auth/login',
  ];

  constructor() {
    // No iniciar automáticamente el monitoreo en el constructor
    // Ahora se debe llamar explícitamente a startMonitoring()

    // Configurar la suscripción a los cambios de ruta
    this.setupRouterSubscription();
  }

  /**
   * Iniciar el monitoreo de inactividad
   */
  startMonitoring(): void {
    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;

    // Configurar la suscripción a los cambios de ruta
    this.setupRouterSubscription();

    // Iniciar el servicio de inactividad
    this.inactivityService.startWatching();
  }

  /**
   * Configurar la suscripción a eventos de navegación
   */
  private setupRouterSubscription(): void {
    // Limpiar suscripción anterior si existe
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }

    // Escuchar cambios de ruta para reiniciar contador
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe((event: NavigationStart) => {
        // Verificar si la ruta es una ruta de logout
        if (this.isLogoutRoute(event.url)) {
          this.stopMonitoring();
          return;
        }

        // Para otras rutas, si el monitoreo está activo, reiniciar contador
        if (this.isInitialized) {
          // Reiniciar el temporizador de inactividad
          // Cada vez que la ruta cambia, considerar como actividad del usuario
          this.inactivityService.continueSession();
        }
      });
  }

  /**
   * Verifica si una ruta es de logout
   * @param url URL a verificar
   * @returns true si es una ruta de logout
   */
  private isLogoutRoute(url: string): boolean {
    // Verifica si la URL coincide con alguna de las rutas de logout conocidas
    return this.logoutRoutes.some((route) => url.includes(route));
  }

  /**
   * Detener el monitoreo de inactividad
   */
  stopMonitoring(): void {
    this.isInitialized = false;
    this.inactivityService.stopWatching();
  }

  /**
   * Limpiar suscripciones al destruir el servicio
   */
  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
      this.routerSubscription = null;
    }
    this.stopMonitoring();
  }
}
